/**
 * Script loading helpers, to be used ONLY to inject script at CLIENT side Two strategies provided, afterInteractive and
 * lazyOnload
 *
 * 1. AfterInteractive: inject script to the end of html head with high priority, will be loaded as soon as possible but
 *    after hydration
 * 2. LazyOnload: inject script to the end of html body, will be loaded during browser idle time and after all resources on
 *    the page have been fetched Cheat sheet for script tag options on async, defer, and module:
 *    https://v8.dev/features/modules#defer More details on
 *    https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script Note that very critical scripts can be added in
 *    xms-platform.config (getBeforeInteractiveScripts) to be loaded before hydration Design doc:
 *    https://docs.google.com/document/d/1sUnqLAUph1oEoAuv6h_PLNbEHvt8zDHt0BA5DqsQVkM/edit
 */
import { requestIdleCallback } from './request-idle-callback';

export type ScriptLoadingOption = {
  async?: boolean;
  defer?: boolean;
  module?: boolean;
};

export type ClientSideScriptLoadingOption = ScriptLoadingOption & {
  onLoad?: (e: any) => void;
  onError?: (e: any) => void;
};

const scriptLoadingHelper = {
  injectClientScript: (
    id: string,
    src: string,
    strategy: 'afterInteractive' | 'lazyOnload',
    options?: ClientSideScriptLoadingOption
  ) => {
    const { onLoad, onError, async, defer } = { ...options };

    // Skip if script ID already exists
    if (document.getElementById(id) !== null) {
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    if (id) {
      script.id = id;
    }
    if (onLoad) {
      script.onload = onLoad;
    }
    if (onError) {
      script.onerror = onError;
    }
    if (async) {
      script.async = true;
    }
    if (defer) {
      script.defer = true;
    }

    script.dataset.strategy = strategy;

    const appendScriptInBody = (script: HTMLScriptElement) =>
      requestIdleCallback(() => {
        // Since this callback happens later on, we check if script ID already exists to ensure we don't inject the same script twice
        if (document.getElementById(id) !== null) {
          return;
        }
        document.body.appendChild(script);
      });

    switch (strategy) {
      case 'afterInteractive': {
        document.head.appendChild(script);
        break;
      }
      case 'lazyOnload': {
        if (document.readyState === 'complete') {
          appendScriptInBody(script);
        } else {
          window.addEventListener('load', () => {
            appendScriptInBody(script);
          });
        }
        break;
      }
      default: {
        throw new Error(`Invalid script loading strategy: ${strategy}`);
      }
    }
  },
};

export default scriptLoadingHelper;
